import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import { useAuth } from "@/context/AuthContext";
import { isManager } from "@/lib/roles";
import { toast } from "sonner";
import { Plus, Trash2, Save, Users, UserRound } from "lucide-react";
import { PARTNER_CATEGORIES as CATEGORIES, CAT_COLOR } from "@/lib/partners";

function PartnerRow({ partner, canEdit, onSaved, onDeleted }) {
  const [p, setP] = useState(partner);
  const [busy, setBusy] = useState(false);
  useEffect(() => setP(partner), [partner]);
  const dirty = JSON.stringify(p) !== JSON.stringify(partner);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/referral-partners/${partner.id}`, {
        name: p.name, category: p.category, contact_person: p.contact_person,
        phone: p.phone, email: p.email, notes: p.notes, active: p.active,
      });
      onSaved(data);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${partner.name}"? Leads keep their record but lose the live link.`)) return;
    try { await api.delete(`/referral-partners/${partner.id}`); onDeleted(partner.id); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!canEdit) {
    return (
      <tr className="border-b border-[#15233B]/8 align-top">
        <td className="py-2 pr-2 font-semibold text-[#15233B]">{p.name}{p.active === false && <span className="ml-1 text-[9px] uppercase text-[#15233B]/40">(inactive)</span>}</td>
        <td className="py-2 px-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${CAT_COLOR[p.category] || CAT_COLOR.Other}`}>{p.category}</span></td>
        <td className="py-2 px-2 text-[#15233B]/80">{p.contact_person || "—"}</td>
        <td className="py-2 px-2 text-[#15233B]/80">{p.phone || "—"}</td>
        <td className="py-2 px-2 text-[#15233B]/80">{p.email || "—"}</td>
        <td className="py-2 px-2 text-[#15233B]/60">{p.notes || "—"}</td>
      </tr>
    );
  }
  return (
    <tr className="border-b border-[#15233B]/8 align-top" data-testid={`partner-row-${partner.id}`}>
      <td className="py-2 pr-2"><input value={p.name} onChange={(e) => set("name", e.target.value)} className="w-full border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white" /></td>
      <td className="py-2 px-2">
        <select value={p.category} onChange={(e) => set("category", e.target.value)} className="border border-[#15233B]/15 rounded px-1.5 py-1 text-xs bg-white">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </td>
      <td className="py-2 px-2"><input value={p.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} className="w-full border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white" /></td>
      <td className="py-2 px-2"><input value={p.phone || ""} onChange={(e) => set("phone", e.target.value)} className="w-28 border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white" /></td>
      <td className="py-2 px-2"><input value={p.email || ""} onChange={(e) => set("email", e.target.value)} className="w-40 border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white" /></td>
      <td className="py-2 px-2"><input value={p.notes || ""} onChange={(e) => set("notes", e.target.value)} className="w-full border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white" /></td>
      <td className="py-2 px-2 text-center"><input type="checkbox" checked={!!p.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 rounded border-[#15233B]/30 text-[#FBBF24] focus:ring-[#FBBF24]" /></td>
      <td className="py-2 pl-2 whitespace-nowrap text-right">
        <button onClick={save} disabled={!dirty || busy} className="text-xs font-semibold text-[#FBBF24] hover:underline disabled:opacity-40 disabled:no-underline inline-flex items-center gap-1"><Save className="h-3 w-3" /> Save</button>
        <button onClick={remove} data-testid={`partner-delete-${partner.id}`} className="ml-3 p-1 rounded text-[#b91c1c] hover:bg-[#b91c1c]/5 inline-flex"><Trash2 className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

function AgentRow({ agent, canEdit, onSaved, onDeleted }) {
  const [a, setA] = useState(agent);
  const [busy, setBusy] = useState(false);
  useEffect(() => setA(agent), [agent]);
  const dirty = JSON.stringify(a) !== JSON.stringify(agent);
  const set = (k, v) => setA((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/field-agents/${agent.id}`, {
        name: a.name, phone: a.phone, email: a.email, notes: a.notes, active: a.active,
      });
      onSaved(data);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${agent.name}"? Leads keep their record but lose the live link.`)) return;
    try { await api.delete(`/field-agents/${agent.id}`); onDeleted(agent.id); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!canEdit) {
    return (
      <tr className="border-b border-[#15233B]/8 align-top">
        <td className="py-2 pr-2 font-semibold text-[#15233B]">{a.name}{a.active === false && <span className="ml-1 text-[9px] uppercase text-[#15233B]/40">(inactive)</span>}</td>
        <td className="py-2 px-2 text-[#15233B]/80">{a.phone || "—"}</td>
        <td className="py-2 px-2 text-[#15233B]/80">{a.email || "—"}</td>
        <td className="py-2 px-2 text-[#15233B]/60">{a.notes || "—"}</td>
      </tr>
    );
  }
  return (
    <tr className="border-b border-[#15233B]/8 align-top" data-testid={`agent-row-${agent.id}`}>
      <td className="py-2 pr-2"><input value={a.name} onChange={(e) => set("name", e.target.value)} className="w-full border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white" /></td>
      <td className="py-2 px-2"><input value={a.phone || ""} onChange={(e) => set("phone", e.target.value)} className="w-32 border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white" /></td>
      <td className="py-2 px-2"><input value={a.email || ""} onChange={(e) => set("email", e.target.value)} className="w-44 border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white" /></td>
      <td className="py-2 px-2"><input value={a.notes || ""} onChange={(e) => set("notes", e.target.value)} className="w-full border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white" /></td>
      <td className="py-2 px-2 text-center"><input type="checkbox" checked={!!a.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 rounded border-[#15233B]/30 text-[#FBBF24] focus:ring-[#FBBF24]" /></td>
      <td className="py-2 pl-2 whitespace-nowrap text-right">
        <button onClick={save} disabled={!dirty || busy} className="text-xs font-semibold text-[#FBBF24] hover:underline disabled:opacity-40 disabled:no-underline inline-flex items-center gap-1"><Save className="h-3 w-3" /> Save</button>
        <button onClick={remove} data-testid={`agent-delete-${agent.id}`} className="ml-3 p-1 rounded text-[#b91c1c] hover:bg-[#b91c1c]/5 inline-flex"><Trash2 className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2 text-sm font-bold rounded-t-md border-b-2 -mb-px transition-colors ${
        active ? "border-[#FBBF24] text-[#15233B]" : "border-transparent text-[#15233B]/45 hover:text-[#15233B]/70"
      }`}
    >
      {children}
    </button>
  );
}

export default function PartnersPage() {
  const { user } = useAuth();
  const canEdit = isManager(user);
  const [tab, setTab] = useState("partners");

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", category: "Building Manager", contact_person: "", phone: "", email: "", notes: "", active: true });

  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentForm, setAgentForm] = useState({ name: "", phone: "", email: "", notes: "", active: true });

  useEffect(() => {
    api.get("/referral-partners").then(({ data }) => { setPartners(data || []); setLoading(false); });
    api.get("/field-agents").then(({ data }) => { setAgents(data || []); setAgentsLoading(false); }).catch(() => setAgentsLoading(false));
  }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      const { data } = await api.post("/referral-partners", form);
      setPartners((p) => [...p, data].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())));
      setForm({ name: "", category: "Building Manager", contact_person: "", phone: "", email: "", notes: "", active: true });
      toast.success("Source added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createAgent = async () => {
    if (!agentForm.name.trim()) { toast.error("Name is required"); return; }
    try {
      const { data } = await api.post("/field-agents", agentForm);
      setAgents((p) => [...p, data].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())));
      setAgentForm({ name: "", phone: "", email: "", notes: "", active: true });
      toast.success("Field agent added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl w-full mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <Users className="h-5 w-5 text-[#FBBF24]" />
          <h1 className="text-2xl font-extrabold tracking-tight text-[#15233B]">Referral Partners &amp; Field Agents</h1>
        </div>
        <p className="text-sm text-[#15233B]/60 mb-5">Manage where your leads come from and the agents who work them.{!canEdit && " (View only — managers and owners can edit.)"}</p>

        <div className="border-b border-[#15233B]/10 mb-5 flex gap-1">
          <TabButton active={tab === "partners"} onClick={() => setTab("partners")} testId="partners-tab">Referral Partners</TabButton>
          <TabButton active={tab === "agents"} onClick={() => setTab("agents")} testId="field-agents-tab">Field Agents</TabButton>
        </div>

        {tab === "partners" && (
          <div data-testid="partners-panel">
            <p className="text-xs text-[#15233B]/55 mb-4">Buildings, doctor&apos;s offices, community resources, campaigns — anywhere a lead can come from. Pick one per lead as its source.</p>
            {canEdit && (
              <div data-testid="partner-add" className="grid grid-cols-12 gap-2 items-end mb-5 bg-white border border-[#15233B]/10 rounded-md p-3">
                <div className="col-span-12 sm:col-span-3"><label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Name</label><input data-testid="partner-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Lincoln Tower Apartments" className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" /></div>
                <div className="col-span-6 sm:col-span-2"><label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Category</label><select data-testid="partner-new-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white">{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                <div className="col-span-6 sm:col-span-2"><label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Contact</label><input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" /></div>
                <div className="col-span-6 sm:col-span-2"><label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Phone</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" /></div>
                <div className="col-span-6 sm:col-span-2"><label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Email</label><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" /></div>
                <div className="col-span-12 sm:col-span-1"><button onClick={create} data-testid="partner-create" className="w-full px-3 py-2 rounded bg-[#FFCB05] text-[#15233B] text-sm font-extrabold hover:bg-[#FBC02D] inline-flex items-center justify-center gap-1"><Plus className="h-4 w-4" /></button></div>
              </div>
            )}
            <div className="bg-white border border-[#15233B]/10 rounded-md p-4 overflow-x-auto">
              {loading ? <div className="text-sm text-[#15233B]/50">Loading…</div> : partners.length === 0 ? (
                <div className="text-sm text-[#15233B]/50 italic">No referral partners yet.{canEdit ? " Add one above." : ""}</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/60">
                    <tr className="border-b border-[#15233B]/10">
                      <th className="text-left py-2 pr-2">Name</th><th className="text-left py-2 px-2">Category</th><th className="text-left py-2 px-2">Contact</th><th className="text-left py-2 px-2">Phone</th><th className="text-left py-2 px-2">Email</th><th className="text-left py-2 px-2">Notes</th>{canEdit && <><th className="text-center py-2 px-2">Active</th><th className="text-right py-2 pl-2">Actions</th></>}
                    </tr>
                  </thead>
                  <tbody>
                    {partners.map((p) => (
                      <PartnerRow key={p.id} partner={p} canEdit={canEdit}
                        onSaved={(d) => setPartners((prev) => prev.map((x) => (x.id === d.id ? d : x)))}
                        onDeleted={(id) => setPartners((prev) => prev.filter((x) => x.id !== id))} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === "agents" && (
          <div data-testid="agents-panel">
            <div className="flex items-center gap-2 mb-1"><UserRound className="h-4 w-4 text-[#1E73C9]" /><span className="text-sm font-bold text-[#15233B]">Field agents</span></div>
            <p className="text-xs text-[#15233B]/55 mb-4">Your marketing / field people. These are NOT app logins — just a list you can tag on a lead. Pick one per lead as the agent who worked it.</p>
            {canEdit && (
              <div data-testid="agent-add" className="grid grid-cols-12 gap-2 items-end mb-5 bg-white border border-[#15233B]/10 rounded-md p-3">
                <div className="col-span-12 sm:col-span-3"><label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Name</label><input data-testid="agent-new-name" value={agentForm.name} onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })} placeholder="e.g., Maria Lopez" className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" /></div>
                <div className="col-span-6 sm:col-span-3"><label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Phone</label><input value={agentForm.phone} onChange={(e) => setAgentForm({ ...agentForm, phone: e.target.value })} className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" /></div>
                <div className="col-span-6 sm:col-span-3"><label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Email</label><input value={agentForm.email} onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })} className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" /></div>
                <div className="col-span-10 sm:col-span-2"><label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Notes</label><input value={agentForm.notes} onChange={(e) => setAgentForm({ ...agentForm, notes: e.target.value })} className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" /></div>
                <div className="col-span-2 sm:col-span-1"><button onClick={createAgent} data-testid="agent-create" className="w-full px-3 py-2 rounded bg-[#FFCB05] text-[#15233B] text-sm font-extrabold hover:bg-[#FBC02D] inline-flex items-center justify-center gap-1"><Plus className="h-4 w-4" /></button></div>
              </div>
            )}
            <div className="bg-white border border-[#15233B]/10 rounded-md p-4 overflow-x-auto">
              {agentsLoading ? <div className="text-sm text-[#15233B]/50">Loading…</div> : agents.length === 0 ? (
                <div className="text-sm text-[#15233B]/50 italic">No field agents yet.{canEdit ? " Add one above." : ""}</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/60">
                    <tr className="border-b border-[#15233B]/10">
                      <th className="text-left py-2 pr-2">Name</th><th className="text-left py-2 px-2">Phone</th><th className="text-left py-2 px-2">Email</th><th className="text-left py-2 px-2">Notes</th>{canEdit && <><th className="text-center py-2 px-2">Active</th><th className="text-right py-2 pl-2">Actions</th></>}
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((a) => (
                      <AgentRow key={a.id} agent={a} canEdit={canEdit}
                        onSaved={(d) => setAgents((prev) => prev.map((x) => (x.id === d.id ? d : x)))}
                        onDeleted={(id) => setAgents((prev) => prev.filter((x) => x.id !== id))} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
      <BrandFooter />
    </div>
  );
}
